import React, { FC, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { Grid } from '@mui/material'
import { IClientTypeWithAssets } from '@sparling/financial-common'
import { ClientTypesFilters } from './ClientTypesFilters'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { columns } from './clientTypesList'
import { IDataItem, TableComponent } from '../../../../components/Table'
import {
  clientTypesLoading,
  createClientTypeModal,
  getClientTypesData,
  updateClientTypeModal,
  viewClientTypeModal,
} from '../../../../store/selectors'
import {
  getClientTypeDetailsRequest,
  getClientTypesRequest,
  openCreateClientTypeModal,
  openUpdateClientTypeModal,
  openViewClientTypeModal,
} from '../../../../store/actions'
import { ClientTypeCreateModal } from './ClientTypesModals/ClientTypeCreateModal'
import { ClientTypeViewModal } from './ClientTypesModals/ClientTypeViewModal'
import { ClientTypeUpdateModal } from './ClientTypesModals/ClientTypeUpdateModal'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0),
    '& p': {
      display: 'none',
    },
  },
  filters: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    minWidth: 400,
  },
}))

export const ClientTypes: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()

  const clientTypesIsLoading = useSelector(clientTypesLoading)
  const { items, meta } = useSelector(getClientTypesData)
  const { open: isOpenCreateModal } = useSelector(createClientTypeModal)
  const { open: isOpenUpdateModal } = useSelector(updateClientTypeModal)
  const { open: isOpenViewModal } = useSelector(viewClientTypeModal)

  const handleOpenCreateModal = useCallback(() => {
    dispatch(openCreateClientTypeModal({ open: true }))
  }, [dispatch])

  const handleCloseCreateModal = useCallback(() => {
    dispatch(openCreateClientTypeModal({ open: false }))
  }, [dispatch])

  const handleOpenViewModal = useCallback(
    (id: number | string | undefined) => {
      if (!id || typeof id !== 'string') return
      dispatch(getClientTypeDetailsRequest({ id }))
      dispatch(openViewClientTypeModal({ open: true }))
    },
    [dispatch],
  )

  const handleCloseViewModal = useCallback(() => {
    dispatch(openViewClientTypeModal({ open: false }))
  }, [dispatch])

  const handleOpenUpdateModal = useCallback(
    (id: string | number) => {
      if (!id || typeof id !== 'string') return

      const clientType = items?.find((item) => item.id === id)
      if (!clientType) return

      dispatch(
        openUpdateClientTypeModal({
          open: true,
          id,
          currentName: clientType.name ?? null,
        }),
      )
    },
    [dispatch, items],
  )

  const handleCloseUpdateModal = useCallback(() => {
    dispatch(openUpdateClientTypeModal({ open: false, id: null, currentName: null }))
  }, [dispatch])

  const loadClientTypesData = useCallback(
    (page = 1) => {
      dispatch(getClientTypesRequest({ page }))
    },
    [dispatch],
  )

  useEffect(() => {
    loadClientTypesData()
  }, [loadClientTypesData])

  useEffect(() => {
    history.push(`?page=1`)
  }, [history])

  return (
    <Grid container>
      <Grid container className={classes.topWrap}>
        <Grid container className={classes.filters}>
          <Grid item xs={10} display={'flex'} alignItems={'center'}>
            <ClientTypesFilters />
          </Grid>
          <Grid item xs={2} display="flex" justifyContent={'flex-end'}>
            <GreenButton width={120} ml={8} text={'Create'} onClick={handleOpenCreateModal} />
          </Grid>
        </Grid>
      </Grid>
      <TableComponent
        columns={columns}
        data={items as IDataItem<IClientTypeWithAssets>[]}
        params={meta}
        loadData={loadClientTypesData}
        isShowEditIcon={true}
        isShowInfoIcon={true}
        onEdit={handleOpenUpdateModal}
        onInfo={handleOpenViewModal}
        loading={clientTypesIsLoading}
      />
      <ClientTypeCreateModal isOpen={isOpenCreateModal} modalClose={handleCloseCreateModal} />
      <ClientTypeViewModal isOpen={isOpenViewModal} modalClose={handleCloseViewModal} />
      <ClientTypeUpdateModal isOpen={isOpenUpdateModal} modalClose={handleCloseUpdateModal} />
    </Grid>
  )
}
